<template>
    <div class="modal-background">
        <div v-show="!isResultView">
            <div class="modal-card pt-6">
                <header class="modal-card-head">
                    <p class="modal-card-title">Message Redaction</p>
                    <button class="delete is-medium" @click="close()" aria-label="close"></button>
                </header>
                <section class="modal-card-body">
                    <div class="content">
                        <p>Manually trigger message and report content redaction for a specific account. This mirrors the nightly redaction job for a single account.</p>
                        <p>Set Redact Before, Redact After, or both. Time is optional &mdash; a date alone is interpreted as midnight UTC.</p>
                    </div>
                    <div class="field">
                        <label class="label">Account Public Id</label>
                        <div class="control">
                            <input class="input" type="number" placeholder="Account public id..." v-model.number="accountPublicId">
                        </div>
                    </div>
                    <div class="field">
                        <label class="label">Redact Before</label>
                        <div class="field has-addons">
                            <div class="control">
                                <input class="input" type="date" v-model="redactBeforeDate">
                            </div>
                            <div class="control">
                                <input class="input" type="time" v-model="redactBeforeTime" :disabled="!redactBeforeDate">
                            </div>
                        </div>
                        <p v-if="redactBeforeInFuture" class="help is-danger">Date must not be in the future.</p>
                    </div>
                    <div class="field">
                        <label class="label">Redact After</label>
                        <div class="field has-addons">
                            <div class="control">
                                <input class="input" type="date" v-model="redactAfterDate">
                            </div>
                            <div class="control">
                                <input class="input" type="time" v-model="redactAfterTime" :disabled="!redactAfterDate">
                            </div>
                        </div>
                        <p v-if="redactAfterInFuture" class="help is-danger">Date must not be in the future.</p>
                        <p v-else-if="redactWindowInvalid" class="help is-danger">Redact After must be earlier than Redact Before.</p>
                    </div>
                    <p v-if="!redactBeforeIso && !redactAfterIso" class="help">At least one of Redact Before or Redact After is required.</p>
                </section>
                <footer class="modal-card-foot">
                    <button :class="isLoading ? 'button is-primary is-loading' : 'button is-primary'" :disabled="!isValid"
                        @click="queueRedaction()">Queue Redaction</button>
                    <button class="button" @click="close()">Cancel</button>
                </footer>
            </div>
        </div>
        <div v-show="isResultView">
            <div class="modal-card pt-6">
                <header class="modal-card-head">
                    <p class="modal-card-title">Result</p>
                    <button class="delete is-medium" @click="close()" aria-label="close"></button>
                </header>
                <section class="modal-card-body">
                    <div class="message is-success">
                        <div class="message-header">
                            <p>Success</p>
                        </div>
                        <div class="message-body">
                            <p>Message redaction job has been queued.</p>
                            <p>Job Id: {{ jobId }}</p>
                        </div>
                    </div>
                </section>
                <footer class="modal-card-foot">
                    <button class="button is-primary" @click="closeResults()">Redact Another Account</button>
                    <button class="button" @click="close()">Close</button>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { securePost } from '../../secureFetch.js';

function buildRedactionIso(dateStr, timeStr) {
    if (!dateStr) return null;
    if (timeStr) {
        return new Date(`${dateStr}T${timeStr}`).toISOString();
    }
    return `${dateStr}T00:00:00.000Z`;
}

export default {
    name: "MessageRedactionModal",
    computed: {
        hostUrl: function () {
            return this.$store.state.hostUrl;
        },
        redactBeforeIso: function () {
            return buildRedactionIso(this.redactBeforeDate, this.redactBeforeTime);
        },
        redactAfterIso: function () {
            return buildRedactionIso(this.redactAfterDate, this.redactAfterTime);
        },
        redactBeforeInFuture: function () {
            return !!this.redactBeforeIso && new Date(this.redactBeforeIso) > new Date();
        },
        redactAfterInFuture: function () {
            return !!this.redactAfterIso && new Date(this.redactAfterIso) > new Date();
        },
        redactWindowInvalid: function () {
            return !!this.redactBeforeIso && !!this.redactAfterIso
                && new Date(this.redactAfterIso) >= new Date(this.redactBeforeIso);
        },
        isValid: function () {
            if (!this.accountPublicId || this.accountPublicId <= 0) return false;
            if (!this.redactBeforeIso && !this.redactAfterIso) return false;
            if (this.redactBeforeInFuture) return false;
            if (this.redactAfterInFuture) return false;
            if (this.redactWindowInvalid) return false;
            return true;
        },
    },
    data() {
        return {
            accountPublicId: null,
            redactBeforeDate: null,
            redactBeforeTime: null,
            redactAfterDate: null,
            redactAfterTime: null,
            isLoading: false,
            isResultView: false,
            errorMessage: null,
            jobId: null,
        };
    },
    methods: {
        close() {
            this.resetForm();
            this.isResultView = false;
            this.errorMessage = null;
            this.jobId = null;
            this.$emit("close");
        },
        closeResults() {
            this.resetForm();
            this.isResultView = false;
        },
        resetForm() {
            this.accountPublicId = null;
            this.redactBeforeDate = null;
            this.redactBeforeTime = null;
            this.redactAfterDate = null;
            this.redactAfterTime = null;
            this.isLoading = false;
            this.errorMessage = null;
            this.jobId = null;
        },
        async queueRedaction() {
            this.isLoading = true;

            const postBody = {
                accountPublicId: this.accountPublicId,
                redactBefore: this.redactBeforeIso,
                redactAfter: this.redactAfterIso,
            };

            const result = await securePost(this.hostUrl + "v1/utilityjob/queue-message-redaction", postBody);

            this.isLoading = false;

            if (result != null && result.success) {
                this.jobId = result.jobId;
                this.isResultView = true;
            } else {
                this.$buefy.toast.open({
                    duration: 5000,
                    message: `${result && result.message ? "Error: " + result.message : "Something went wrong."}`,
                    position: 'is-bottom',
                    type: 'is-danger',
                });
            }
        },
    },
};
</script>

<style scoped>
.modal-card {
    overflow: visible;
    max-width: 600px;
    width: fit-content;
}

.modal-card-body {
    overflow: visible;
    max-width: 560px;
    width: fit-content;
}
</style>
